import * as Sentry from "@sentry/react-router";
import { type IDBPDatabase, openDB } from "idb";
import { unwrap } from "~/utils/zod";
import {
	baseCheckoutSchema,
	type CheckoutInput,
	checkoutSchema,
} from "./checkout.schemas";

const DB_NAME = "checkout-db";
const DB_STORE_NAME = "checkout";
const DB_VERSION = 1;

type IndexedDB = IDBPDatabase<{
	[DB_STORE_NAME]: {
		key: string; // User ID
		value: string; // Encrypted data
	};
}>;

interface CheckoutDb {
	get(key: string): Promise<string | undefined> | (string | undefined);
	put(key: string, value: string): Promise<void> | void;
	delete(key: string): Promise<void> | void;
}

function createIndexedDbAdapter(db: IndexedDB): CheckoutDb {
	return {
		get: (key) => db.get(DB_STORE_NAME, key),
		put: async (key, value) => {
			await db.put(DB_STORE_NAME, value, key);
		},
		delete: (key) => db.delete(DB_STORE_NAME, key),
	};
}

function createSessionStorageFallback(): CheckoutDb {
	const prefix = `${DB_NAME}:`;
	return {
		get: (key) => sessionStorage.getItem(prefix + key) ?? undefined,
		put: (key, value) => {
			sessionStorage.setItem(prefix + key, value);
		},
		delete: (key) => {
			sessionStorage.removeItem(prefix + key);
		},
	};
}

export class CheckoutStore {
	private readonly dbPromise: Promise<IndexedDB> | null;
	private store: CheckoutDb | null = null;

	constructor() {
		if (typeof window !== "undefined" && window.indexedDB) {
			this.dbPromise = openDB(DB_NAME, DB_VERSION, {
				upgrade(db) {
					if (!db.objectStoreNames.contains(DB_STORE_NAME)) {
						db.createObjectStore(DB_STORE_NAME);
					}
				},
			});
		} else {
			this.dbPromise = null;
		}
	}

	async get(userId: string) {
		try {
			const store = await this.getStore();
			const base64Data = await store.get(userId);
			if (!base64Data) return null;

			const data = this.decodeBase64ToObject(base64Data);
			if (!data) return null;

			const parsed = checkoutSchema.parse(data);

			return parsed;
		} catch (error) {
			Sentry.addBreadcrumb({
				category: "checkout-store",
				message: "Error getting checkout data",
				data: { userId, error },
			});
			Sentry.captureException(error);
			return null;
		}
	}

	async set<K extends keyof Omit<CheckoutInput, "expireAt">>(
		userId: string,
		key: K,
		value: CheckoutInput[K],
	) {
		try {
			const schema = unwrap(baseCheckoutSchema.shape[key]);
			const parsed = schema.safeParse(value);
			if (!parsed.success) {
				throw new Error(`Invalid data: ${parsed.error.message}`);
			}

			const current = await this.get(userId);
			if (!current) {
				throw new Error(
					"Cannot update checkout: existing data is unreadable or missing",
				);
			}
			const updatedData = {
				...current,
				[key]: parsed.data,
			};

			const base64Data = this.encodeObjectToBase64(updatedData);

			const store = await this.getStore();
			await store.put(userId, base64Data);
		} catch (error) {
			Sentry.addBreadcrumb({
				category: "checkout-store",
				message: "Error setting checkout data",
				data: { userId, key, error },
			});
			Sentry.captureException(error);
			throw error;
		}
	}

	async initialize(userId: string, value: Omit<CheckoutInput, "expireAt">) {
		try {
			const parsed = checkoutSchema.safeParse(value);
			if (!parsed.success) {
				throw new Error(`Invalid data: ${parsed.error.message}`);
			}

			const base64Data = this.encodeObjectToBase64(parsed.data);

			const store = await this.getStore();
			await store.put(userId, base64Data);
		} catch (error) {
			Sentry.addBreadcrumb({
				category: "checkout-store",
				message: "Error initializing checkout data",
				data: { userId, error },
			});
			Sentry.captureException(error);
			throw error;
		}
	}

	async delete(userId: string) {
		try {
			const store = await this.getStore();
			await store.delete(userId);
		} catch (error) {
			Sentry.addBreadcrumb({
				category: "checkout-store",
				message: "Error deleting checkout data",
				data: { userId, error },
			});
			Sentry.captureException(error);
			return null;
		}
	}

	private async getStore(): Promise<CheckoutDb> {
		if (this.store) return this.store;

		if (this.dbPromise) {
			try {
				const db = await this.dbPromise;
				this.store = createIndexedDbAdapter(db);
				return this.store;
			} catch (error) {
				Sentry.addBreadcrumb({
					category: "checkout-store",
					message: "IndexedDB unavailable, falling back to sessionStorage",
					level: "warning",
					data: { error },
				});
			}
		}

		this.store = createSessionStorageFallback();
		return this.store;
	}

	private encodeObjectToBase64(data: Record<string, unknown>) {
		try {
			const jsonString = JSON.stringify(data);
			const utf8Bytes = new TextEncoder().encode(jsonString);
			const base64String = btoa(String.fromCharCode(...utf8Bytes));
			return base64String;
		} catch (error) {
			Sentry.addBreadcrumb({
				category: "checkout-store",
				message: "Error encoding object to Base64",
				data: { error },
			});
			Sentry.captureException(error);
			throw error;
		}
	}

	private decodeBase64ToObject(base64String: string) {
		try {
			const binaryString = atob(base64String);
			const binaryBytes = Uint8Array.from(binaryString, (char) =>
				char.charCodeAt(0),
			);
			const jsonString = new TextDecoder().decode(binaryBytes);
			return JSON.parse(jsonString);
		} catch (error) {
			Sentry.addBreadcrumb({
				category: "checkout-store",
				message: "Error decoding Base64 to object",
				data: { error },
			});
			Sentry.captureException(error);
			throw error;
		}
	}
}
